import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, 
  X,
  AlertCircle
} from 'lucide-react';


const RevenueModeRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('PENDING');

  useEffect(() => {
    fetchRequests();
  }, [filterStatus]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      let url = `/api/admin/revenue-mode-requests`;
      if (filterStatus !== 'ALL') {
        url = `/api/admin/revenue-mode-requests?status=${filterStatus}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors du chargement des demandes');
      }
      
      const data = await response.json();
      setRequests(data.requests || []);
    } catch (error) {
      console.error('Erreur:', error);
      setError('Impossible de charger les demandes.');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm('Voulez-vous vraiment approuver cette demande ?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/revenue-mode-requests/${id}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ note: 'Approuvé par le support' })
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de l\'approbation');
      }
      
      alert('Demande approuvée avec succès');
      fetchRequests();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Voulez-vous vraiment rejeter cette demande ?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/revenue-mode-requests/${id}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: 'Rejeté par le support' })
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors du rejet');
      }
      
      alert('Demande rejetée avec succès');
      fetchRequests();
    } catch (error) {
      alert(error.message);
    }
  };

  return (
    <div style={styles.container} className="rmr-container">
      <style>{`
        .rmr-table-scroll {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          width: 100%;
        }
        @media (max-width: 768px) {
          .rmr-container {
            padding: 12px 8px !important;
          }
          .rmr-header {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 16px !important;
            margin-bottom: 1.25rem !important;
          }
          .rmr-filters {
            width: 100% !important;
          }
          .rmr-filters select {
            width: 100% !important;
          }
          .rmr-title {
            font-size: 1.4rem !important;
          }
        }
      `}</style>
      <div style={styles.header} className="rmr-header">
        <div>
          <h1 style={styles.title} className="rmr-title">Demandes d'Abonnement</h1>
          <p style={styles.subtitle}>Gérez les demandes de changement entre Commission et Abonnement</p>
        </div>
        
        <div style={styles.filters} className="rmr-filters">
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            style={styles.select}
          >
            <option value="ALL">Tous les statuts</option>
            <option value="PENDING">En attente (PENDING)</option>
            <option value="APPROVED">Approuvé (APPROVED)</option>
            <option value="REJECTED">Rejeté (REJECTED)</option>
          </select>
        </div>
      </div>

      {error && (
        <div style={styles.error}>
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <p>Chargement des demandes...</p>
      ) : requests.length === 0 ? (
        <div style={styles.emptyState}>
          <p>Aucune demande trouvée.</p>
        </div>
      ) : (
        <div style={styles.tableContainer}>
          <div className="rmr-table-scroll">
            <table style={{ ...styles.table, minWidth: '650px' }}>
              <thead>
                <tr>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Chauffeur</th>
                  <th style={styles.th}>Mode Actuel</th>
                  <th style={styles.th}>Mode Demandé</th>
                  <th style={styles.th}>Statut</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(req => (
                  <tr key={req.id} style={styles.tr}>
                    <td style={styles.td}>
                      {new Date(req.requestedAt).toLocaleString()}
                    </td>
                    <td style={styles.td}>
                      {req.driverName || 'Inconnu'}<br/>
                      <small style={{ color: '#6b7280' }}>{req.driverEmail}</small>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.badge(req.currentMode === 'SUBSCRIPTION' ? '#8b5cf6' : '#f59e0b')}>
                        {req.currentMode === 'SUBSCRIPTION' ? 'Abonnement' : 'Commission'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.badge(req.requestedMode === 'SUBSCRIPTION' ? '#8b5cf6' : '#f59e0b')}>
                        {req.requestedMode === 'SUBSCRIPTION' ? 'Abonnement' : 'Commission'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.badge(req.status === 'APPROVED' ? '#10b981' : req.status === 'REJECTED' ? '#ef4444' : '#f59e0b')}>
                        {req.status}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {req.status === 'PENDING' && (
                        <div style={styles.actions}>
                          <button 
                            onClick={() => handleApprove(req.id)}
                            style={{ ...styles.btn, backgroundColor: '#10b981', color: 'white' }}
                          >
                            <CheckCircle size={16} />
                          </button>
                          <button 
                            onClick={() => handleReject(req.id)}
                            style={{ ...styles.btn, backgroundColor: '#ef4444', color: 'white' }}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  title: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
  },
  subtitle: {
    color: '#64748b',
    marginTop: '0.5rem',
  },
  filters: {
    display: 'flex',
    gap: '1rem',
  },
  select: {
    padding: '0.5rem 1rem',
    borderRadius: '0.5rem',
    border: '1px solid #e2e8f0',
    backgroundColor: 'white',
    outline: 'none',
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '1rem',
    backgroundColor: '#fef2f2',
    color: '#ef4444',
    borderRadius: '0.5rem',
    marginBottom: '1rem',
  },
  tableContainer: {
    backgroundColor: 'white',
    borderRadius: '1rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '1rem',
    textAlign: 'left',
    backgroundColor: '#f8fafc',
    color: '#475569',
    fontWeight: '600',
    borderBottom: '1px solid #e2e8f0',
  },
  td: {
    padding: '1rem',
    borderBottom: '1px solid #e2e8f0',
    verticalAlign: 'middle',
  },
  tr: {
    '&:hover': {
      backgroundColor: '#f8fafc',
    }
  },
  badge: (color) => ({
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    backgroundColor: `${color}20`,
    color: color,
    fontSize: '0.875rem',
    fontWeight: '500',
  }),
  actions: {
    display: 'flex',
    gap: '0.5rem',
  },
  btn: {
    padding: '0.5rem',
    borderRadius: '0.5rem',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.2s',
  },
  emptyState: {
    textAlign: 'center',
    padding: '4rem',
    backgroundColor: 'white',
    borderRadius: '1rem',
    color: '#64748b',
  }
};

export default RevenueModeRequests;
